import {
  DEFAULT_ASSISTANT_LLM_TYPE,
  FALLBACK_ASSISTANT_LLM_TYPE,
  LLMType,
} from "models/enums/LLMType";
import { normalizeModelKey } from "utils/generalUtils";

export const STORAGE_KEY_USER_LLM_SETTINGS = "userLLMSettings";
export const STORAGE_KEY_SONNET_55_MIGRATED = "userLLMSettingsSonnet55Migrated";
export const SONNET_55_MIGRATED_FLAG = "true";

interface IModelNameHolder {
  name?: string;
  displayName?: string;
}

export interface ISavedLlmSettingsMigrationResult {
  savedRaw: string | null;
  hasRemapped: boolean;
  previousLlmType?: string;
  migratedLlmType?: string;
}

// Large context has no 5.5 variant, so it maps to the plain 5.5 model.
const LEGACY_TO_REPLACEMENT_MODEL: ReadonlyMap<string, LLMType> = new Map([
  [normalizeModelKey(LLMType.CLAUDE_SONNET_4_5), LLMType.CLAUDE_SONNET_5_5],
  [
    normalizeModelKey(LLMType.CLAUDE_SONNET_4_5_LARGE_CONTEXT),
    LLMType.CLAUDE_SONNET_5_5,
  ],
  [
    normalizeModelKey(LLMType.CLAUDE_SONNET_4_5_WEB_SEARCH),
    LLMType.CLAUDE_SONNET_5_5_WEB_SEARCH,
  ],
]);

const REPLACEMENT_MODEL_KEYS: ReadonlySet<string> = new Set(
  Array.from(LEGACY_TO_REPLACEMENT_MODEL.values(), normalizeModelKey),
);

const hasModelNamed = (
  availableModels: IModelNameHolder[],
  modelName: string,
): boolean =>
  availableModels.some(
    (model) => normalizeModelKey(model.name) === normalizeModelKey(modelName),
  );

const hasAnySonnet55Model = (availableModels: IModelNameHolder[]): boolean =>
  availableModels.some((model) =>
    REPLACEMENT_MODEL_KEYS.has(normalizeModelKey(model.name)),
  );

const readStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

// The browser can be shared, so the marker is kept per user when known.
const getMigrationMarkerKey = (userId?: string): string =>
  userId
    ? `${STORAGE_KEY_SONNET_55_MIGRATED}:${userId}`
    : STORAGE_KEY_SONNET_55_MIGRATED;

/**
 * Returns the Sonnet 5.5 equivalent of a Sonnet 4.5 model name, or undefined
 * when the name is not a Sonnet 4.5 variant. Matching ignores case, spaces and
 * hyphens. Does not check availability.
 */
export const getReplacementModelName = (
  modelName?: string,
): LLMType | undefined =>
  LEGACY_TO_REPLACEMENT_MODEL.get(normalizeModelKey(modelName));

/**
 * Remaps a Sonnet 4.5 model name to its 5.5 equivalent, but only when that
 * 5.5 model is in the user's available models. Otherwise the original value
 * is returned untouched (the 5.5 record may still be a limited canary).
 */
export const remapLegacyModelName = (
  modelName: string,
  availableModels: IModelNameHolder[],
): string => {
  const replacement = getReplacementModelName(modelName);
  if (!replacement || !hasModelNamed(availableModels, replacement)) {
    return modelName;
  }
  return replacement;
};

/**
 * Finds the model a template recommends. Templates usually store the model
 * display name (for example "Claude Sonnet 4.5 - Problem Solver"), so the
 * model is resolved first and its name is then remapped to 5.5 when the user
 * can see 5.5. Falls back to the originally recommended model otherwise.
 */
export const resolveRecommendedModel = <T extends IModelNameHolder>(
  availableModels: T[],
  recommendedModel?: string,
): T | undefined => {
  const recommendedKey = normalizeModelKey(recommendedModel);
  if (!recommendedKey) {
    return undefined;
  }

  const recommended =
    availableModels.find(
      (model) =>
        normalizeModelKey(model.displayName || model.name) === recommendedKey,
    ) ??
    availableModels.find(
      (model) => normalizeModelKey(model.name) === recommendedKey,
    );

  const remappedName = remapLegacyModelName(
    recommended?.name ?? recommendedModel ?? "",
    availableModels,
  );
  const remapped = availableModels.find(
    (model) =>
      normalizeModelKey(model.name) === normalizeModelKey(remappedName),
  );
  return remapped ?? recommended;
};

/**
 * Picks the model used by chats that send a fixed default instead of the
 * user's selection. Prefers Sonnet 5.5 only when the user can see it; while
 * the models are not loaded yet (or 5.5 is not visible) it uses Sonnet 4.5.
 * Callers must re-evaluate this when the available models change.
 */
export const resolveAssistantLlmType = (
  availableModels: IModelNameHolder[],
): LLMType =>
  hasModelNamed(availableModels, DEFAULT_ASSISTANT_LLM_TYPE)
    ? DEFAULT_ASSISTANT_LLM_TYPE
    : FALLBACK_ASSISTANT_LLM_TYPE;

/**
 * Remaps the llmType inside a saved "userLLMSettings" JSON string. Returns
 * the updated JSON string, or undefined when nothing changed (including
 * unparseable input), so callers only write back when needed.
 */
export const migrateSavedLlmSettings = (
  savedRaw: string | null,
  availableModels: IModelNameHolder[],
): string | undefined => {
  if (!savedRaw) {
    return undefined;
  }

  try {
    const saved = JSON.parse(savedRaw);
    if (typeof saved?.llmType !== "string") {
      return undefined;
    }

    const remapped = remapLegacyModelName(saved.llmType, availableModels);
    if (remapped === saved.llmType) {
      return undefined;
    }

    return JSON.stringify({ ...saved, llmType: remapped });
  } catch {
    return undefined;
  }
};

/**
 * Records that the Sonnet 4.5 -> 5.5 migration window is closed for the user,
 * so a later deliberate choice of 4.5 is never remapped. Only does so once a
 * 5.5 model is visible to the user; before that nothing is recorded.
 */
export const markSonnet55MigrationComplete = (
  availableModels: IModelNameHolder[],
  userId?: string,
): void => {
  const isSonnet55Visible = hasAnySonnet55Model(availableModels);
  if (isSonnet55Visible) {
    writeStorageItem(getMigrationMarkerKey(userId), SONNET_55_MIGRATED_FLAG);
  }
};

/**
 * One-time localStorage remap of the saved model selection from Sonnet 4.5 to
 * 5.5. It runs on the first call where a 5.5 model is visible to the user and
 * sets the marker on that call whether or not anything was remapped, so any
 * later explicit pick (including 4.5) stays as chosen. While no 5.5 model is
 * visible nothing is touched and the marker is not set.
 */
export const applySavedLlmSettingsMigration = (
  availableModels: IModelNameHolder[],
  userId?: string,
): ISavedLlmSettingsMigrationResult => {
  const savedRaw = readStorageItem(STORAGE_KEY_USER_LLM_SETTINGS);
  const markerKey = getMigrationMarkerKey(userId);
  if (readStorageItem(markerKey) === SONNET_55_MIGRATED_FLAG) {
    return { savedRaw, hasRemapped: false };
  }

  const isSonnet55Visible = hasAnySonnet55Model(availableModels);
  if (!isSonnet55Visible) {
    return { savedRaw, hasRemapped: false };
  }

  writeStorageItem(markerKey, SONNET_55_MIGRATED_FLAG);

  const migratedRaw = migrateSavedLlmSettings(savedRaw, availableModels);
  if (!migratedRaw) {
    return { savedRaw, hasRemapped: false };
  }

  writeStorageItem(STORAGE_KEY_USER_LLM_SETTINGS, migratedRaw);
  return {
    savedRaw: migratedRaw,
    hasRemapped: true,
    previousLlmType: JSON.parse(savedRaw as string).llmType,
    migratedLlmType: JSON.parse(migratedRaw).llmType,
  };
};
